import React from "react";
import { Stack, Typography } from "@mui/material";
import { borderStyle, iconGap, perelynPrimary, smallGap, tinyGap } from "../../styles/consts";
import { BiggerThanSmallAndSmallerThanMediumText, SmallText } from "../../common/styles/Text.styles";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EuroIcon from '@mui/icons-material/Euro';
import PercentIcon from '@mui/icons-material/Percent';
import ComputerIcon from '@mui/icons-material/Computer';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import ApartmentIcon from '@mui/icons-material/Apartment';

interface JobDetailsProps {
  offer: {
    start?: string;
    dauer?: string;
    stundensatz?: string;
    auslastung?: string;
    einsatzart?: string;
    vertragsart?: string;
    einsatzort?: string;
    branche?: string;
    primary_job_language?: string;
    company?: string;
  };
}

export default function JobDetails({ offer }: JobDetailsProps) {
  const formatValue = (value: string | undefined, defaultValue = "Not specified") => {
    if (!value || value === "Nach Vereinbarung") return defaultValue;
    return value;
  };

  const DetailItem = ({ icon, label, value }: { icon: React.ReactElement; label: string; value: string }) => {
    return (
      <Stack sx={{ flexDirection: "row", alignItems: "flex-start", gap: tinyGap }}>
        {React.cloneElement(icon, {
          sx: {
            width: "1.25rem",
            height: "1.25rem",
            color: perelynPrimary,
            flexShrink: 0,
            marginTop: "2px"
          }
        })}
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <SmallText sx={{ color: "#666", fontSize: "0.75rem" }}>{label}</SmallText>
          <Typography sx={{ fontSize: "0.875rem", wordBreak: "break-word" }}>
            {value}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  return (
    <Stack
      sx={{
        border: borderStyle,
        padding: smallGap,
        backgroundColor: "white",
        gap: iconGap,
      }}
    >
      <BiggerThanSmallAndSmallerThanMediumText sx={{ color: perelynPrimary, marginBottom: tinyGap }}>
        Job Details
      </BiggerThanSmallAndSmallerThanMediumText>
      
      <Stack sx={{ gap: iconGap }}>
        {/* First row */}
        <Stack sx={{ flexDirection: "row", gap: smallGap }}>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<CalendarMonthIcon />}
              label="Start Date"
              value={formatValue(offer.start)}
            />
          </Stack>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<AccessTimeIcon />}
              label="Duration"
              value={formatValue(offer.dauer)}
            />
          </Stack>
        </Stack>

        {/* Second row */}
        <Stack sx={{ flexDirection: "row", gap: smallGap }}>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<EuroIcon />}
              label="Rate"
              value={formatValue(offer.stundensatz)}
            />
          </Stack>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<PercentIcon />}
              label="Workload"
              value={formatValue(offer.auslastung)}
            />
          </Stack>
        </Stack>

        {/* Third row */}
        <Stack sx={{ flexDirection: "row", gap: smallGap }}>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<ComputerIcon />}
              label="Work Type"
              value={formatValue(offer.einsatzart)}
            />
          </Stack>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<DescriptionIcon />}
              label="Contract Type"
              value={formatValue(offer.vertragsart)}
            />
          </Stack>
        </Stack>

        {/* Fourth row */}
        <Stack sx={{ flexDirection: "row", gap: smallGap }}>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<LocationOnIcon />}
              label="Location"
              value={formatValue(offer.einsatzort)}
            />
          </Stack>
          <Stack sx={{ flex: 1 }}>
            <DetailItem
              icon={<LanguageIcon />}
              label="Language"
              value={formatValue(offer.primary_job_language)}
            />
          </Stack>
        </Stack>

        {/* Fifth row - optional fields */}
        {(offer.branche || offer.company) && (
          <Stack sx={{ flexDirection: "row", gap: smallGap }}>
            {offer.branche && (
              <Stack sx={{ flex: 1 }}>
                <DetailItem
                  icon={<BusinessIcon />}
                  label="Industry"
                  value={formatValue(offer.branche)}
                />
              </Stack>
            )}
            {offer.company && (
              <Stack sx={{ flex: 1 }}>
                <DetailItem
                  icon={<ApartmentIcon />}
                  label="Company"
                  value={formatValue(offer.company)}
                />
              </Stack>
            )}
            {/* Add empty flex item if only one field exists */}
            {(!offer.branche || !offer.company) && <Stack sx={{ flex: 1 }} />}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}